import React, { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Text,
  View,
} from '../ui';
import type {
  OptimizationModalInputs,
  SpeedOptimizationTarget,
} from '../core/speed-optimization';
import { speedOptimizationTargetSchema } from '../core/speed-optimization';
import { pluralize } from '../../lib/format';
import { z } from 'zod';

export type SpeedOptimizationModalProps = {
  onCancel: () => void;
  onComplete: (params: OptimizationModalInputs) => void;
  /** Initial values for the form */
  initialValues?: Partial<OptimizationModalInputs>;
  /** Number of legs that will be optimized */
  legCount: number;
  /** Available speeds from the vessel's speed consumption data */
  availableSpeeds: number[];
};

const TARGET_OPTIONS = [
  { value: 'tce', label: 'Maximize TCE' },
  { value: 'minimize_delays', label: 'Minimize delays and violations' },
  { value: 'minimize_costs', label: 'Minimize fuel costs' },
] satisfies { value: SpeedOptimizationTarget; label: string }[];

type SpeedInputFieldProps = {
  name: 'min' | 'max';
  control: Control<OptimizationModalInputs>;
  speedOptions: { value: string; label: string }[];
  placeholder?: string;
  error?: string;
};

const speedOptimizationFormSchema = z.object({
  target: speedOptimizationTargetSchema,
  speedConstraints: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
    })
    .refine(
      (data) => {
        if (data.min !== undefined && data.max !== undefined) {
          return data.min <= data.max;
        }
        return true;
      },
      {
        message: 'Minimum speed must be less than or equal to maximum speed',
        path: ['min'],
      },
    ),
  operationBuffers: z.object({
    loadingStart: z.number(),
    loadingEnd: z.number(),
    dischargingStart: z.number(),
    dischargingEnd: z.number(),
  }),
});

const SpeedInputField: React.FC<SpeedInputFieldProps> = ({
  name,
  control,
  speedOptions,
  placeholder,
  error,
}) => (
  <View>
    <Controller
      name={`speedConstraints.${name}`}
      control={control}
      render={({ field }) => (
        <Select
          value={field.value?.toString() ?? ''}
          onChange={({ target: { value } }) => {
            const numValue = value === '' ? undefined : Number(value);
            field.onChange(numValue);
          }}
          options={speedOptions}
          placeholder={placeholder}
        />
      )}
    />
    {error && (
      <Text color="error" fontSize={1} mt={1}>
        {error}
      </Text>
    )}
  </View>
);

export const SpeedOptimizationModal: React.FC<SpeedOptimizationModalProps> = ({
  onCancel,
  onComplete,
  initialValues,
  legCount,
  availableSpeeds,
}) => {
  const defaultValues = useMemo((): OptimizationModalInputs => {
    return {
      target: 'tce',
      speedConstraints: {
        min: Math.min(...availableSpeeds),
        max: Math.max(...availableSpeeds),
      },
      operationBuffers: {
        loadingStart: 0,
        loadingEnd: 0,
        dischargingStart: 0,
        dischargingEnd: 0,
      },
      ...initialValues,
    };
  }, [availableSpeeds, initialValues]);

  const form = useForm<OptimizationModalInputs>({
    resolver: zodResolver(speedOptimizationFormSchema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = form;

  const onSubmit = handleSubmit((data) => {
    onComplete(data);
  });

  const speedOptions = useMemo(
    () =>
      availableSpeeds?.map((speed) => ({
        value: speed.toString(),
        label: `${speed} knots`,
      })) ?? [],
    [availableSpeeds],
  );

  const subtitle = pluralize`Optimizing voyage speeds for ${legCount} leg`;

  return (
    <Modal
      isOpen
      onRequestClose={onCancel}
      onClose={onCancel}
      width="600px"
      maxWidth="90vw"
    >
      <form onSubmit={onSubmit}>
        <ModalBody>
          <View mb={4}>
            <Text fontSize={5} fontWeight="bold" mb={2}>
              Speed Optimization
            </Text>
            <Text color="text.secondary" fontSize={2}>
              {subtitle}
            </Text>
          </View>

          <View mb={5}>
            <Text fontSize={3} fontWeight="semibold" mb={3}>
              Optimization Target
            </Text>
            <Controller
              name="target"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={({ target: { value } }) =>
                    field.onChange(speedOptimizationTargetSchema.parse(value))
                  }
                  options={[...TARGET_OPTIONS]}
                  placeholder="Select optimization target"
                  disabled={true}
                />
              )}
            />
            <Text color="text.secondary" fontSize={1} mt={2}>
              Only TCE optimization is currently implemented.
            </Text>
          </View>

          <View mb={5}>
            <Text fontSize={3} fontWeight="semibold" mb={3}>
              Speed Constraints
            </Text>
            <View
              display="grid"
              style={{
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px 20px',
                alignItems: 'start',
              }}
            >
              <View>
                <Text fontSize={2} fontWeight="medium" mb={2}>
                  Minimum Speed (knots)
                </Text>
                <SpeedInputField
                  name="min"
                  control={control}
                  speedOptions={speedOptions}
                  error={errors.speedConstraints?.min?.message}
                />
              </View>
              <View>
                <Text fontSize={2} fontWeight="medium" mb={2}>
                  Maximum Speed (knots)
                </Text>
                <SpeedInputField
                  name="max"
                  control={control}
                  speedOptions={speedOptions}
                  error={errors.speedConstraints?.max?.message}
                />
              </View>
            </View>
          </View>

          <View mb={5}>
            <Text fontSize={3} fontWeight="semibold" mb={2}>
              Operation Window Buffers (hours)
            </Text>
            <Text color="text.secondary" fontSize={2} mb={3}>
              Adjust loading and discharging windows. Positive values tighten
              windows, negative values allow more flexibility.
            </Text>

            <View
              display="grid"
              style={{
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px 20px',
                alignItems: 'start',
              }}
            >
              <View>
                <Text fontSize={2} fontWeight="medium" mb={2}>
                  Loading Window Start
                </Text>
                <Input
                  type="number"
                  {...register('operationBuffers.loadingStart', {
                    valueAsNumber: true,
                  })}
                  step={0.5}
                  placeholder="0"
                />
              </View>
              <View>
                <Text fontSize={2} fontWeight="medium" mb={2}>
                  Loading Window End
                </Text>
                <Input
                  type="number"
                  {...register('operationBuffers.loadingEnd', {
                    valueAsNumber: true,
                  })}
                  step={0.5}
                  placeholder="0"
                />
              </View>
              <View>
                <Text fontSize={2} fontWeight="medium" mb={2}>
                  Discharging Window Start
                </Text>
                <Input
                  type="number"
                  {...register('operationBuffers.dischargingStart', {
                    valueAsNumber: true,
                  })}
                  step={0.5}
                  placeholder="0"
                />
              </View>
              <View>
                <Text fontSize={2} fontWeight="medium" mb={2}>
                  Discharging Window End
                </Text>
                <Input
                  type="number"
                  {...register('operationBuffers.dischargingEnd', {
                    valueAsNumber: true,
                  })}
                  step={0.5}
                  placeholder="0"
                />
              </View>
            </View>
          </View>

          <View p={4} backgroundColor="background.secondary" borderRadius={1}>
            <Text fontSize={2} color="text.secondary">
              <strong>Note:</strong> Speed optimization finds optimal speeds
              that maximize TCE while respecting operation window constraints.
              The optimization will set sea duration overrides for the selected
              legs and may remove conflicting start time overrides.
            </Text>
          </View>
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onPress={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onPress={onSubmit}>
            Optimize Speed
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
